/**
 * AffDice SuperAdmin messages — the Operators, Brands and Affiliates pages' shared
 * parts: the actions panel, the delete / restore / purge dialog, the list
 * filters and the deleted and purged states.
 *
 * Every key starts `ent.` so no other catalogue can collide with it.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const ENTITIES_MESSAGES = {
  // The actions panel on an operator, brand or affiliate
  'ent.actions.label': { en: 'Actions', es: 'Acciones', 'pt-BR': 'Ações' },
  'ent.actions.readOnly': {
    en: 'You can view this but not change it. Ask the platform super admin for the permissions you need.',
    es: 'Puedes verlo pero no cambiarlo. Pide al superadministrador de la plataforma los permisos que necesitas.',
    'pt-BR': 'Você pode ver isto, mas não alterar. Peça ao superadministrador da plataforma as permissões de que precisa.',
  },
  'ent.actions.none': {
    en: 'Nothing to do here in its current state.',
    es: 'Nada que hacer aquí en su estado actual.',
    'pt-BR': 'Nada a fazer aqui no estado atual.',
  },
  'ent.actions.purged': {
    en: 'Permanently removed. Its money records are kept, with personal details taken out.',
    es: 'Eliminado de forma definitiva. Sus registros de dinero se conservan, sin datos personales.',
    'pt-BR': 'Removido em definitivo. Os registros de dinheiro são mantidos, sem dados pessoais.',
  },

  // States shown beside a name
  'ent.state.deleted': { en: 'Deleted', es: 'Eliminado', 'pt-BR': 'Excluído' },
  'ent.state.purged': { en: 'Purged', es: 'Purgado', 'pt-BR': 'Purgado' },
  'ent.state.deletedOn': { en: 'Deleted on', es: 'Eliminado el', 'pt-BR': 'Excluído em' },
  'ent.state.purgedOn': { en: 'Purged on', es: 'Purgado el', 'pt-BR': 'Purgado em' },

  // Delete, restore and purge dialog
  'ent.life.title.delete': { en: 'Delete {name}', es: 'Eliminar {name}', 'pt-BR': 'Excluir {name}' },
  'ent.life.title.restore': { en: 'Restore {name}', es: 'Restaurar {name}', 'pt-BR': 'Restaurar {name}' },
  'ent.life.title.purge': { en: 'Purge {name}', es: 'Purgar {name}', 'pt-BR': 'Purgar {name}' },
  'ent.life.title.purgeNow': {
    en: 'Delete {name} permanently',
    es: 'Eliminar {name} definitivamente',
    'pt-BR': 'Excluir {name} definitivamente',
  },
  'ent.life.note.delete': {
    en: 'Deleting switches it off and hides it at once. It can be restored until it is purged. Money records are kept.',
    es: 'Al eliminar se desactiva y se oculta de inmediato. Se puede restaurar hasta que se purgue. Los registros de dinero se conservan.',
    'pt-BR': 'Ao excluir, ele é desativado e ocultado na hora. Pode ser restaurado até ser purgado. Os registros de dinheiro são mantidos.',
  },
  'ent.life.note.restore': {
    en: 'Restoring brings it back exactly as it was before it was deleted.',
    es: 'Al restaurar vuelve exactamente como estaba antes de eliminarse.',
    'pt-BR': 'Ao restaurar, volta exatamente como estava antes de ser excluído.',
  },
  'ent.life.note.purge': {
    en: 'Purging removes it for good and takes personal details out of its records; money records stay. It cannot be undone, and is refused until the retention window has passed.',
    es: 'Purgar lo elimina para siempre y quita los datos personales de sus registros; los registros de dinero permanecen. No se puede deshacer y se rechaza hasta que pase el plazo de conservación.',
    'pt-BR': 'Purgar remove em definitivo e tira os dados pessoais dos registros; os registros de dinheiro permanecem. Não pode ser desfeito e é recusado até passar o prazo de retenção.',
  },
  'ent.life.note.purgeNow': {
    en: 'This removes it for good, now, without waiting out the retention window, and takes personal details out of its records; money records stay. It cannot be undone.',
    es: 'Esto lo elimina para siempre, ahora, sin esperar el plazo de conservación, y quita los datos personales de sus registros; los registros de dinero permanecen. No se puede deshacer.',
    'pt-BR': 'Isto remove em definitivo, agora, sem esperar o prazo de retenção, e tira os dados pessoais dos registros; os registros de dinheiro permanecem. Não pode ser desfeito.',
  },
  'ent.life.effect.operator': {
    en: 'Its team and affiliates can no longer sign in, and new traffic stops.',
    es: 'Su equipo y sus afiliados ya no pueden iniciar sesión y el tráfico nuevo se detiene.',
    'pt-BR': 'A equipe e os afiliados não conseguem mais entrar, e o tráfego novo é interrompido.',
  },
  'ent.life.effect.brand': {
    en: 'No new links are issued for it, and staff limited to this brand lose access to it.',
    es: 'No se emiten enlaces nuevos para ella y el personal limitado a esta marca pierde el acceso.',
    'pt-BR': 'Nenhum link novo é emitido para ela, e a equipe limitada a esta marca perde o acesso.',
  },
  'ent.life.effect.affiliate': {
    en: 'This affiliate can no longer sign in to this operator and gets no new links. Their other operators are not affected.',
    es: 'Este afiliado ya no puede iniciar sesión en este operador y no recibe enlaces nuevos. Sus otros operadores no se ven afectados.',
    'pt-BR': 'Este afiliado não consegue mais entrar neste operador e não recebe links novos. Os outros operadores dele não são afetados.',
  },
  'ent.life.confirmName': { en: 'Type the name to confirm', es: 'Escribe el nombre para confirmar', 'pt-BR': 'Digite o nome para confirmar' },
  'ent.life.confirmNameHint': {
    en: 'Exactly as written: {name}',
    es: 'Exactamente como está escrito: {name}',
    'pt-BR': 'Exatamente como está escrito: {name}',
  },
  'ent.life.action.delete': { en: 'Delete', es: 'Eliminar', 'pt-BR': 'Excluir' },
  'ent.life.action.restore': { en: 'Restore', es: 'Restaurar', 'pt-BR': 'Restaurar' },
  'ent.life.action.purge': { en: 'Purge', es: 'Purgar', 'pt-BR': 'Purgar' },
  'ent.life.action.purgeNow': { en: 'Delete permanently', es: 'Eliminar definitivamente', 'pt-BR': 'Excluir definitivamente' },
  'ent.life.busy.delete': { en: 'Deleting…', es: 'Eliminando…', 'pt-BR': 'Excluindo…' },
  'ent.life.busy.restore': { en: 'Restoring…', es: 'Restaurando…', 'pt-BR': 'Restaurando…' },
  'ent.life.busy.purge': { en: 'Purging…', es: 'Purgando…', 'pt-BR': 'Purgando…' },

  // Operators page
  'ent.operators.filter.all': { en: 'All operators', es: 'Todos los operadores', 'pt-BR': 'Todos os operadores' },
  'ent.operators.filter.live': { en: 'Live', es: 'En producción', 'pt-BR': 'Em produção' },
  'ent.operators.filter.setup': { en: 'Being set up', es: 'En configuración', 'pt-BR': 'Em configuração' },
  'ent.operators.filter.suspended': {
    en: 'Suspended or restricted',
    es: 'Suspendidos o restringidos',
    'pt-BR': 'Suspensos ou restritos',
  },
  'ent.operators.filter.awaiting': { en: 'Awaiting approval', es: 'Pendientes de aprobación', 'pt-BR': 'Aguardando aprovação' },
  'ent.operators.filter.deleted': { en: 'Deleted', es: 'Eliminados', 'pt-BR': 'Excluídos' },

  // Brands page
  'ent.brands.title': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },
  'ent.brands.panel': { en: 'Every brand', es: 'Todas las marcas', 'pt-BR': 'Todas as marcas' },
  'ent.brands.searchPlaceholder': {
    en: 'Search by brand, code, tracking domain or operator',
    es: 'Buscar por marca, código, dominio de seguimiento u operador',
    'pt-BR': 'Buscar por marca, código, domínio de rastreamento ou operador',
  },
  'ent.brands.filter.all': { en: 'All brands', es: 'Todas las marcas', 'pt-BR': 'Todas as marcas' },
  'ent.brands.filter.active': { en: 'Active', es: 'Activas', 'pt-BR': 'Ativas' },
  'ent.brands.filter.inactive': { en: 'Inactive', es: 'Inactivas', 'pt-BR': 'Inativas' },
  'ent.brands.filter.deleted': { en: 'Deleted or purged', es: 'Eliminadas o purgadas', 'pt-BR': 'Excluídas ou purgadas' },
  'ent.brands.none': {
    en: 'No brands yet. An operator adds its brands from its own console.',
    es: 'Aún no hay marcas. Cada operador añade las suyas desde su propia consola.',
    'pt-BR': 'Ainda não há marcas. Cada operador adiciona as suas no próprio console.',
  },
  'ent.brands.noMatch': {
    en: 'No brand matches this search and these filters.',
    es: 'Ninguna marca coincide con esta búsqueda y estos filtros.',
    'pt-BR': 'Nenhuma marca corresponde a esta busca e a estes filtros.',
  },
  'ent.brands.note': {
    en: 'Deleted brands are listed last. Open one to restore or purge it. Opening a brand reads its operator’s figures and settings, and that read is logged.',
    es: 'Las marcas eliminadas aparecen al final. Abre una para restaurarla o purgarla. Abrir una marca lee las cifras y ajustes de su operador, y esa lectura queda registrada.',
    'pt-BR': 'As marcas excluídas aparecem por último. Abra uma para restaurá-la ou purgá-la. Abrir uma marca lê os números e as configurações do operador, e essa leitura fica registrada.',
  },
  'ent.brand.websiteUrl': { en: 'Website', es: 'Sitio web', 'pt-BR': 'Site' },
  // The brand's own web app (owner, 2026-10-12): the same words as Admin's.
  'ent.brand.defaultUrl': { en: 'Web app URL', es: 'URL de la aplicación web', 'pt-BR': 'URL do aplicativo web' },
  'ent.brand.colour': { en: 'Colour', es: 'Color', 'pt-BR': 'Cor' },
  'ent.brand.deletedNote': {
    en: 'This brand is deleted, so its figures, affiliates and settings are hidden. Restore it to see them again.',
    es: 'Esta marca está eliminada, así que sus cifras, afiliados y ajustes están ocultos. Restáurala para volver a verlos.',
    'pt-BR': 'Esta marca está excluída, então os números, afiliados e configurações ficam ocultos. Restaure-a para vê-los de novo.',
  },
  'ent.brand.purgedNote': {
    en: 'This brand was purged and cannot be restored. Its money records remain, with personal details removed.',
    es: 'Esta marca fue purgada y no se puede restaurar. Sus registros de dinero permanecen, sin datos personales.',
    'pt-BR': 'Esta marca foi purgada e não pode ser restaurada. Os registros de dinheiro permanecem, sem dados pessoais.',
  },

  // Affiliates page
  'ent.affiliates.filter.all': { en: 'All affiliates', es: 'Todos los afiliados', 'pt-BR': 'Todos os afiliados' },
  'ent.affiliates.filter.active': { en: 'Active', es: 'Activos', 'pt-BR': 'Ativos' },
  'ent.affiliates.filter.pending': {
    en: 'Pending or under review',
    es: 'Pendientes o en revisión',
    'pt-BR': 'Pendentes ou em análise',
  },
  'ent.affiliates.filter.inactive': {
    en: 'Deactivated, suspended or blocked',
    es: 'Desactivados, suspendidos o bloqueados',
    'pt-BR': 'Desativados, suspensos ou bloqueados',
  },
  'ent.affiliates.filter.suspended': {
    en: 'Suspended everywhere',
    es: 'Suspendidos en todas partes',
    'pt-BR': 'Suspensos em todos os lugares',
  },
  'ent.affiliates.filter.deleted': { en: 'Deleted or purged', es: 'Eliminados o purgados', 'pt-BR': 'Excluídos ou purgados' },
  'ent.affiliates.inlineDeleted': { en: '(deleted)', es: '(eliminado)', 'pt-BR': '(excluído)' },
  'ent.affiliates.inlinePurged': { en: '(purged)', es: '(purgado)', 'pt-BR': '(purgado)' },
  'ent.affiliate.deletedNote': {
    en: 'This profile is deleted: the affiliate cannot sign in to this operator and gets no new links. Restore it to bring it back as it was.',
    es: 'Este perfil está eliminado: el afiliado no puede iniciar sesión en este operador ni recibe enlaces nuevos. Restáuralo para recuperarlo como estaba.',
    'pt-BR': 'Este perfil está excluído: o afiliado não consegue entrar neste operador nem recebe links novos. Restaure-o para voltar como estava.',
  },
  'ent.affiliate.purgedNote': {
    en: 'This profile was purged and cannot be restored. Its money records remain, with personal details removed.',
    es: 'Este perfil fue purgado y no se puede restaurar. Sus registros de dinero permanecen, sin datos personales.',
    'pt-BR': 'Este perfil foi purgado e não pode ser restaurado. Os registros de dinheiro permanecem, sem dados pessoais.',
  },
} as const satisfies Catalogue;
